import { useState, useCallback } from "react";
import { validarComprovanteOCR } from "@/services/ocr";
import type { OCRStatus, OCRValidationInput, OCRValidationResponse } from "@/types/ocr";

const CONFIANCA_MINIMA = 0.8;

/**
 * Hook que encapsula o fluxo de validação OCR com estados bem definidos.
 *
 * Estados:
 *   - idle: ainda não validado
 *   - processing: aguardando resposta do OCR
 *   - success: OCR validou com alta confiança (ambos match + confiança >= 80%)
 *   - warning: OCR LEU mas algum dado não bateu com o digitado (divergência —
 *              permitir envio mesmo assim; quem julga é o operador, RN-1)
 *   - ilegivel: OCR NÃO CONSEGUIU LER valor ou data (foto ruim — só o parceiro
 *              resolve, tirando outra foto). Distinto de `warning` porque a
 *              trava de legibilidade barra só este caso.
 *   - error: falha de rede ou erro inesperado
 */
export function useOCRValidation() {
  const [status, setStatus] = useState<OCRStatus>("idle");
  const [result, setResult] = useState<OCRValidationResponse | null>(null);

  const validar = useCallback(
    async (input: OCRValidationInput): Promise<OCRValidationResponse | null> => {
      setStatus("processing");
      setResult(null);
      try {
        const response = await validarComprovanteOCR(input);
        setResult(response);

        if (!response.sucesso) {
          setStatus("warning");
          return response;
        }

        // ILEGÍVEL antes de tudo: o campo não foi EXTRAÍDO, independentemente
        // de bater ou não com o digitado. É a única condição que a trava barra.
        if (!response.valor?.detectado || !response.data_hora) {
          setStatus("ilegivel");
          return response;
        }

        const confiancaOK = response.confianca_global >= CONFIANCA_MINIMA;
        const valorOK = response.valor.match;
        const terminalOK = response.terminal.match;

        setStatus(confiancaOK && valorOK && terminalOK ? "success" : "warning");
        return response;
      } catch {
        setStatus("error");
        return null;
      }
    },
    []
  );

  const reset = useCallback(() => {
    setStatus("idle");
    setResult(null);
  }, []);

  return { status, result, validar, reset };
}
